import { useState, useEffect, useCallback } from 'react';
import { motion } from 'motion/react';
import { 
  Crown, 
  Clock, 
  CheckCircle, 
  XCircle, 
  Eye, 
  ChevronDown, 
  ChevronUp, 
  MapPin, 
  Phone, 
  Mail, 
  Instagram, 
  Calendar,
  Users,
  FileText,
  Loader2,
  Filter
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  pending: { label: 'Pending', color: 'bg-amber-100 text-amber-800', icon: Clock },
  reviewed: { label: 'Reviewed', color: 'bg-blue-100 text-blue-800', icon: Eye },
  approved: { label: 'Approved', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  declined: { label: 'Declined', color: 'bg-red-100 text-red-800', icon: XCircle }
};

const ApplicationCard = ({ application, onStatusChange }) => {
  const [expanded, setExpanded] = useState(false);
  const [notes, setNotes] = useState(application.admin_notes || '');
  const [updating, setUpdating] = useState(false);

  const statusConfig = STATUS_CONFIG[application.status] || STATUS_CONFIG.pending;
  const StatusIcon = statusConfig.icon;

  const handleStatusUpdate = async (newStatus) => {
    setUpdating(true);
    try {
      const response = await fetch(
        `${API_URL}/api/postmaster/admin/applications/${application.application_id}?status=${newStatus}&notes=${encodeURIComponent(notes)}`,
        {
          method: 'PUT',
          credentials: 'include'
        }
      );
      
      if (response.ok) {
        onStatusChange();
      }
    } catch (error) {
      console.error('Error updating status:', error);
    } finally {
      setUpdating(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white border border-stone-200 rounded-lg overflow-hidden"
      data-testid={`application-card-${application.application_id}`}
    >
      {/* Header */}
      <div 
        className="p-4 cursor-pointer hover:bg-stone-50 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
              <Crown className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <h3 className="font-medium text-stone-800">{application.applicant.full_name}</h3>
              <p className="text-sm text-stone-500">
                {application.applicant.city}, {application.applicant.state} • {application.application_id}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <span className={`px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${statusConfig.color}`}>
              <StatusIcon className="w-3 h-3" />
              {statusConfig.label}
            </span>
            <span className="text-xs text-stone-400">
              {formatDate(application.submitted_at)}
            </span>
            {expanded ? (
              <ChevronUp className="w-5 h-5 text-stone-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-stone-400" />
            )}
          </div>
        </div>
      </div>

      {/* Expanded Content */}
      {expanded && (
        <div className="border-t border-stone-200 p-4 space-y-6">
          {/* Contact Info */}
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-stone-700 flex items-center gap-2">
                <Mail className="w-4 h-4" /> Contact
              </h4>
              <div className="bg-stone-50 rounded-lg p-3 space-y-1 text-sm">
                <p className="flex items-center gap-2">
                  <Mail className="w-3 h-3 text-stone-400" />
                  <a href={`mailto:${application.applicant.email}`} className="text-purple-600 hover:underline">
                    {application.applicant.email}
                  </a>
                </p>
                <p className="flex items-center gap-2">
                  <Phone className="w-3 h-3 text-stone-400" />
                  {application.applicant.phone}
                </p>
                {application.applicant.instagram && (
                  <p className="flex items-center gap-2">
                    <Instagram className="w-3 h-3 text-stone-400" />
                    {application.applicant.instagram}
                  </p>
                )}
                <p className="flex items-center gap-2">
                  <MapPin className="w-3 h-3 text-stone-400" />
                  {application.applicant.city}, {application.applicant.state}
                </p>
              </div>
            </div>

            <div className="space-y-2">
              <h4 className="text-sm font-medium text-stone-700 flex items-center gap-2">
                <Users className="w-4 h-4" /> LWS Connection
              </h4>
              <div className="bg-stone-50 rounded-lg p-3 space-y-1 text-sm">
                <p><span className="text-stone-500">Relationship:</span> {application.connection.relationship}</p>
                <p><span className="text-stone-500">Attended gathering:</span> {application.connection.attended_gathering ? 'Yes' : 'No'}</p>
                {application.connection.gathering_details && (
                  <p><span className="text-stone-500">Details:</span> {application.connection.gathering_details}</p>
                )}
                <p><span className="text-stone-500">How heard:</span> {application.applicant.how_heard}</p>
              </div>
            </div>
          </div>

          {/* Chapter Plans */}
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-stone-700 flex items-center gap-2">
              <Calendar className="w-4 h-4" /> Chapter Plans
            </h4>
            <div className="bg-purple-50 rounded-lg p-3 space-y-1 text-sm">
              <p><span className="text-purple-700">Format:</span> {application.chapter.format}</p>
              <p><span className="text-purple-700">Frequency:</span> {application.chapter.frequency}</p>
              {application.chapter.ideal_day && (
                <p><span className="text-purple-700">Ideal day:</span> {application.chapter.ideal_day}</p>
              )}
              {application.chapter.venue_ideas && (
                <p><span className="text-purple-700">Venue ideas:</span> {application.chapter.venue_ideas}</p>
              )}
            </div>
          </div>

          {/* Vision */}
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-stone-700 flex items-center gap-2">
              <FileText className="w-4 h-4" /> Vision & Motivation
            </h4>
            <div className="space-y-3">
              <div className="bg-stone-50 rounded-lg p-3">
                <p className="text-xs text-stone-500 mb-1">Why they want to be a Postmaster:</p>
                <p className="text-sm text-stone-700 italic">&ldquo;{application.vision.why_postmaster}&rdquo;</p>
              </div>
              <div className="bg-stone-50 rounded-lg p-3">
                <p className="text-xs text-stone-500 mb-1">Community vision:</p>
                <p className="text-sm text-stone-700 italic">&ldquo;{application.vision.community_vision}&rdquo;</p>
              </div>
            </div>
          </div>

          {/* Availability */}
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-stone-700">Availability & Commitment</h4>
            <div className="bg-stone-50 rounded-lg p-3 space-y-1 text-sm">
              <p>
                <span className="text-stone-500">Can commit monthly:</span>{' '}
                <span className={application.availability.can_commit ? 'text-green-600' : 'text-amber-600'}>
                  {application.availability.can_commit ? 'Yes, absolutely' : 'Would like to discuss'}
                </span>
              </p>
              <p><span className="text-stone-500">Start timeline:</span> {application.availability.start_timeline}</p>
              {application.availability.questions && (
                <p><span className="text-stone-500">Questions:</span> {application.availability.questions}</p>
              )}
            </div>
          </div>

          {/* Admin Notes */}
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-stone-700">Admin Notes</h4>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add internal notes about this application..."
              className="w-full p-3 border border-stone-200 rounded-lg text-sm resize-none h-20 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between pt-4 border-t border-stone-200">
            <p className="text-xs text-stone-400">
              Last updated: {formatDate(application.updated_at)}
            </p>
            <div className="flex gap-2">
              {application.status !== 'reviewed' && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleStatusUpdate('reviewed')}
                  disabled={updating}
                  className="text-blue-600 border-blue-200 hover:bg-blue-50"
                >
                  <Eye className="w-4 h-4 mr-1" />
                  Mark Reviewed
                </Button>
              )}
              {application.status !== 'approved' && (
                <Button
                  size="sm"
                  onClick={() => handleStatusUpdate('approved')}
                  disabled={updating}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  <CheckCircle className="w-4 h-4 mr-1" />
                  Approve
                </Button>
              )}
              {application.status !== 'declined' && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleStatusUpdate('declined')}
                  disabled={updating}
                  className="text-red-600 border-red-200 hover:bg-red-50"
                >
                  <XCircle className="w-4 h-4 mr-1" />
                  Decline
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default function AdminPostmasterPage() {
  const { user } = useAuth();
  const [applications, setApplications] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const fetchApplications = useCallback(async () => {
    setLoading(true);
    try {
      const url = filter === 'all' 
        ? `${API_URL}/api/postmaster/admin/applications`
        : `${API_URL}/api/postmaster/admin/applications?status=${filter}`;
      
      const response = await fetch(url, { credentials: 'include' });
      const data = await response.json();
      
      setApplications(data.applications || []);
      setStats(data.stats || {});
    } catch (error) {
      console.error('Error fetching applications:', error);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    if (user?.role === 'admin') {
      fetchApplications();
    }
  }, [user, fetchApplications]);

  if (!user || user.role !== 'admin') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <div className="text-center">
          <Crown className="w-12 h-12 text-stone-300 mx-auto mb-4" />
          <h1 className="text-xl font-serif text-stone-600">Admin Access Required</h1>
          <p className="text-stone-400 mt-2">Please log in as an administrator.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50" data-testid="admin-postmaster-page">
      {/* Header */}
      <section className="bg-gradient-to-b from-purple-900 to-purple-800 text-white py-12 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center gap-3 mb-4">
            <Crown className="w-8 h-8" />
            <h1 className="text-3xl font-serif">Postmaster Applications</h1>
          </div>
          <p className="text-purple-200">Review and manage applications for the Postmaster tier</p>
        </div>
      </section>

      {/* Stats */}
      <section className="py-8 px-6 border-b border-stone-200 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <button
              onClick={() => setFilter('all')}
              className={`p-4 rounded-lg border-2 transition-colors ${
                filter === 'all' ? 'border-purple-500 bg-purple-50' : 'border-stone-200 hover:border-stone-300'
              }`}
            >
              <p className="text-2xl font-serif text-stone-800">{stats.total || 0}</p>
              <p className="text-xs text-stone-500 uppercase tracking-wide">Total</p>
            </button>
            <button
              onClick={() => setFilter('pending')}
              className={`p-4 rounded-lg border-2 transition-colors ${
                filter === 'pending' ? 'border-amber-500 bg-amber-50' : 'border-stone-200 hover:border-stone-300'
              }`}
            >
              <p className="text-2xl font-serif text-amber-600">{stats.pending || 0}</p>
              <p className="text-xs text-stone-500 uppercase tracking-wide">Pending</p>
            </button>
            <button
              onClick={() => setFilter('reviewed')}
              className={`p-4 rounded-lg border-2 transition-colors ${
                filter === 'reviewed' ? 'border-blue-500 bg-blue-50' : 'border-stone-200 hover:border-stone-300'
              }`}
            >
              <p className="text-2xl font-serif text-blue-600">{stats.reviewed || 0}</p>
              <p className="text-xs text-stone-500 uppercase tracking-wide">Reviewed</p>
            </button>
            <button
              onClick={() => setFilter('approved')}
              className={`p-4 rounded-lg border-2 transition-colors ${
                filter === 'approved' ? 'border-green-500 bg-green-50' : 'border-stone-200 hover:border-stone-300'
              }`}
            >
              <p className="text-2xl font-serif text-green-600">{stats.approved || 0}</p>
              <p className="text-xs text-stone-500 uppercase tracking-wide">Approved</p>
            </button>
            <button
              onClick={() => setFilter('declined')}
              className={`p-4 rounded-lg border-2 transition-colors ${
                filter === 'declined' ? 'border-red-500 bg-red-50' : 'border-stone-200 hover:border-stone-300'
              }`}
            >
              <p className="text-2xl font-serif text-red-600">{stats.declined || 0}</p>
              <p className="text-xs text-stone-500 uppercase tracking-wide">Declined</p>
            </button>
          </div>
        </div>
      </section>

      {/* Applications List */}
      <section className="py-8 px-6">
        <div className="max-w-5xl mx-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
            </div>
          ) : applications.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-lg border border-stone-200">
              <Crown className="w-12 h-12 text-stone-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-stone-600">No applications found</h3>
              <p className="text-stone-400 mt-1">
                {filter === 'all' 
                  ? 'Applications will appear here when submitted.' 
                  : `No ${filter} applications at the moment.`}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {applications.map((app) => (
                <ApplicationCard 
                  key={app.application_id} 
                  application={app} 
                  onStatusChange={fetchApplications}
                />
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
